import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { LogIn, ExternalLink } from "lucide-react";

const STATUS_VARIANT = {
  verified: "bg-success text-success-foreground",
  submitted: "bg-warning text-warning-foreground",
  pending: "bg-muted text-muted-foreground",
  rejected: "bg-destructive text-destructive-foreground",
};

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [impersonating, setImpersonating] = useState(null);

  useEffect(() => {
    api
      .get("/admin/users")
      .then(({ data }) => setUsers(data || []))
      .catch((err) =>
        toast.error(formatApiError(err.response?.data?.detail) || "Failed to load partners"),
      )
      .finally(() => setLoading(false));
  }, []);

  const loginAsUser = async (userId) => {
    setImpersonating(userId);
    try {
      const { data } = await api.post(`/admin/impersonate/${userId}`);
      // Get the partner portal base URL (remove /api prefix, use the partner domain)
      const baseUrl = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/+$/, "");
      // Open partner portal in new tab with the impersonated token
      const partnerUrl = `${baseUrl}/impersonate?token=${encodeURIComponent(data.access_token)}`;
      window.open(partnerUrl, "_blank", "noopener");
      toast.success(`Opened portal as ${data.user.full_name || data.user.email}`);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to impersonate");
    } finally {
      setImpersonating(null);
    }
  };

  return (
    <DashboardLayout>
      <div>
        <span className="overline">Admin</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Partners
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          All partner accounts on FlexPay, including KYC status.
        </p>

        <div className="mt-8 surface-card overflow-hidden">
          <Table data-testid="partners-table">
            <TableHeader>
              <TableRow>
                <TableHead>Partner</TableHead>
                <TableHead>Email</TableHead>
                <TableHead>Mobile</TableHead>
                <TableHead>Role</TableHead>
                <TableHead>KYC</TableHead>
                <TableHead>Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    Loading…
                  </TableCell>
                </TableRow>
              ) : users.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-10 text-muted-foreground">
                    No partners yet.
                  </TableCell>
                </TableRow>
              ) : (
                users.map((u) => (
                  <TableRow key={u.id} data-testid={`partner-row-${u.id}`}>
                    <TableCell>
                      <div className="font-medium">{u.full_name}</div>
                      <div className="text-xs text-muted-foreground">
                        {u.business_name || "—"}
                      </div>
                    </TableCell>
                    <TableCell>{u.email}</TableCell>
                    <TableCell className="font-mono">{u.mobile}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className="uppercase font-mono text-[10px]">
                        {u.role}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={STATUS_VARIANT[u.kyc_status] || ""}>
                        {u.kyc_status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {u.created_at?.slice(0, 10)}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => loginAsUser(u.id)}
                        disabled={impersonating === u.id}
                        data-testid={`impersonate-${u.id}`}
                      >
                        <LogIn className="h-3.5 w-3.5 mr-1" />
                        {impersonating === u.id ? "Opening…" : "Login as"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </DashboardLayout>
  );
}
